/**
 * Scroll Physics Utility Functions
 * Handles velocity calculations, momentum, and easing for smooth scrolling
 */

// Physics constants for realistic scrolling behavior
export const PHYSICS_CONFIG = {
  // Friction coefficients for different scenarios
  FRICTION: {
    NORMAL: 0.88,      // More aggressive deceleration
    FAST: 0.82,        // Much more aggressive for high-velocity scrolling
    SLOW: 0.92,        // Still smooth for slow scrolling
    BOUNDARY: 0.75     // Strong boundary friction
  },

  // Velocity thresholds
  VELOCITY: {
    THRESHOLD: 0.3,    // Stop animation below this velocity
    MAX: 2,            // Moderate cap for maximum velocity
    MIN_FOR_MOMENTUM: 100 // Set very high to effectively disable momentum for now
  },

  // Bounce physics at boundaries
  BOUNCE: {
    STRENGTH: 0.3,     // How far to overshoot
    FRICTION: 0.7      // Bounce decay rate
  },

  // Performance settings
  PERFORMANCE: {
    THROTTLE_MS: 16,   // ~60fps
    MAX_POINTS: 5,     // Track last N points for velocity calc
    TIME_WINDOW: 100   // ms window for velocity calculation
  }
};

/**
 * Calculate velocity from a series of points
 * @param {Array} points - Array of {x, y, timestamp} objects
 * @returns {Object} - {x: velocityX, y: velocityY}
 */
export const calculateVelocity = (points) => {
  if (points.length < 2) {
    return { x: 0, y: 0 };
  }

  // Use only recent points within time window
  const now = Date.now();
  const recentPoints = points.filter(
    point => (now - point.timestamp) <= PHYSICS_CONFIG.PERFORMANCE.TIME_WINDOW
  );

  if (recentPoints.length < 2) {
    return { x: 0, y: 0 };
  }

  const first = recentPoints[0];
  const last = recentPoints[recentPoints.length - 1];
  const timeDelta = last.timestamp - first.timestamp;

  if (timeDelta === 0) {
    return { x: 0, y: 0 };
  }

  const velocityX = (last.x - first.x) / timeDelta;
  const velocityY = (last.y - first.y) / timeDelta;

  // Apply velocity scaling to reduce excessive speeds
  const velocityScale = 0.1; // Reduce velocity by 90% - more reasonable

  // Cap velocity to prevent excessive speed
  const cappedVelocityX = Math.max(-PHYSICS_CONFIG.VELOCITY.MAX, Math.min(PHYSICS_CONFIG.VELOCITY.MAX, velocityX * velocityScale));
  const cappedVelocityY = Math.max(-PHYSICS_CONFIG.VELOCITY.MAX, Math.min(PHYSICS_CONFIG.VELOCITY.MAX, velocityY * velocityScale));

  // Debug logging for initial velocity calculation
  if (Math.abs(cappedVelocityX) > 10 || Math.abs(cappedVelocityY) > 10) {
    console.warn('High initial velocity:', {
      raw: { x: velocityX, y: velocityY },
      capped: { x: cappedVelocityX, y: cappedVelocityY },
      timeDelta,
      scale: velocityScale
    });
  }

  return {
    x: cappedVelocityX,
    y: cappedVelocityY
  };
};

/**
 * Get appropriate friction based on velocity and context
 * @param {number} velocity - Current velocity
 * @param {boolean} atBoundary - Whether at scroll boundary
 * @returns {number} - Friction coefficient
 */
export const getFriction = (velocity, atBoundary = false) => {
  if (atBoundary) {
    return PHYSICS_CONFIG.FRICTION.BOUNDARY;
  }

  const absVelocity = Math.abs(velocity);

  if (absVelocity > 10) {
    return PHYSICS_CONFIG.FRICTION.FAST;
  } else if (absVelocity < 2) {
    return PHYSICS_CONFIG.FRICTION.SLOW;
  }

  return PHYSICS_CONFIG.FRICTION.NORMAL;
};

/**
 * Easing function for smooth deceleration
 * @param {number} t - Progress (0 to 1)
 * @returns {number} - Eased value
 */
export const easeOutCubic = (t) => {
  return 1 - Math.pow(1 - t, 3);
};

/**
 * Easing function for bounce effect
 * @param {number} t - Progress (0 to 1)
 * @returns {number} - Eased value with bounce
 */
export const easeOutBounce = (t) => {
  if (t < 1 / 2.75) {
    return 7.5625 * t * t;
  } else if (t < 2 / 2.75) {
    return 7.5625 * (t -= 1.5 / 2.75) * t + 0.75;
  } else if (t < 2.5 / 2.75) {
    return 7.5625 * (t -= 2.25 / 2.75) * t + 0.9375;
  } else {
    return 7.5625 * (t -= 2.625 / 2.75) * t + 0.984375;
  }
};

/**
 * Calculate boundary behavior (overshoot and bounce back)
 * @param {number} position - Current position
 * @param {number} velocity - Current velocity
 * @param {number} min - Minimum boundary
 * @param {number} max - Maximum boundary
 * @returns {Object} - {position, velocity, atBoundary}
 */
export const calculateBoundaryBehavior = (position, velocity, min, max) => {
  let newPosition = position;
  let newVelocity = velocity;
  let atBoundary = false;

  // Check boundaries - clamp position and stop velocity (no bounce for now)
  if (position > max) {
    newPosition = max;
    newVelocity = 0; // Stop completely at boundary
    atBoundary = true;
  }
  // Check lower boundary
  else if (position < min) {
    newPosition = min;
    newVelocity = 0; // Stop completely at boundary
    atBoundary = true;
  }

  return {
    position: newPosition,
    velocity: newVelocity,
    atBoundary
  };
};

/**
 * Check if momentum scrolling should be triggered
 * @param {number} velocity - Current velocity
 * @returns {boolean} - Whether to start momentum scrolling
 */
export const shouldTriggerMomentum = (velocity) => {
  return Math.abs(velocity) >= PHYSICS_CONFIG.VELOCITY.MIN_FOR_MOMENTUM;
};

/**
 * Calculate next frame values for momentum scrolling
 * @param {Object} current - {position, velocity}
 * @param {Object} bounds - {min, max}
 * @param {number} deltaTime - Time since last frame (ms)
 * @returns {Object} - {position, velocity, shouldStop, atBoundary}
 */
export const calculateNextFrame = (current, bounds, deltaTime = 16) => {
  let { position, velocity } = current;
  const { min, max } = bounds;

  // Apply velocity to position (velocity is pixels per frame, not per ms)
  const newPosition = position + velocity;

  // Check boundaries and apply bounce
  const boundaryResult = calculateBoundaryBehavior(newPosition, velocity, min, max);

  // Apply friction
  const friction = getFriction(boundaryResult.velocity, boundaryResult.atBoundary);
  const newVelocity = boundaryResult.velocity * friction;

  // Determine if animation should stop
  const shouldStop = Math.abs(newVelocity) < PHYSICS_CONFIG.VELOCITY.THRESHOLD;

  // Debug logging for excessive velocity
  if (Math.abs(newVelocity) > 20) {
    console.warn('High velocity detected:', newVelocity, 'shouldStop:', shouldStop);
  }

  return {
    position: boundaryResult.position,
    velocity: newVelocity,
    shouldStop,
    atBoundary: boundaryResult.atBoundary
  };
};

/**
 * Throttle function for performance optimization
 * @param {Function} func - Function to throttle
 * @param {number} delay - Throttle delay in ms
 * @returns {Function} - Throttled function
 */
export const throttle = (func, delay = PHYSICS_CONFIG.PERFORMANCE.THROTTLE_MS) => {
  let timeoutId;
  let lastExecTime = 0;

  return function (...args) {
    const currentTime = Date.now();

    if (currentTime - lastExecTime > delay) {
      func.apply(this, args);
      lastExecTime = currentTime;
    } else {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
        lastExecTime = Date.now();
      }, delay - (currentTime - lastExecTime));
    }
  };
};

/**
 * Debounce function for event handling
 * @param {Function} func - Function to debounce
 * @param {number} delay - Debounce delay in ms
 * @returns {Function} - Debounced function
 */
export const debounce = (func, delay = 150) => {
  let timeoutId;

  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
};